import { ConfigurationService } from "./ConfigurationService";
import { auth } from "stores/AuthorizationStore";
import axios from "axios";

interface TokenResponse {
  isAuthenticated: boolean;
  accessToken: string;
  expires_at: string;
  id_token: string;
}

export class AuthenticationService {
  private configService: ConfigurationService;
  private pathAccountMe: string = "account/me";
  private isLoggedInStorageKey: string = "isLoggedIn";
  private isLoggedIn: boolean = true;
  private authData: TokenResponse | null = null;
  private retryCount: number = 0;

  constructor() {
    this.configService = new ConfigurationService();
  }

  initialize = async () => {
    await this.getToken();

    window.addEventListener("storage", this.onStorageChanged);
  };

  getAuthData() {
    return this.authData;
  }

  getToken = async () => {
    if (!this.isLoggedIn) {
      // if we were already logged out once, don't allow to log in again in the same instance.
      // User needs to reload page to log in again.
      auth.setIsLoggedOutModalOn(true);
      return;
    }

    try {
      const selfUrl =
        this.configService !== null && this.configService.getWindowOrigin();
      const requestURL = `${selfUrl}/${this.pathAccountMe}`;
      const response = await axios.get(requestURL, {
        maxRedirects: 0,
        withCredentials: true,
      });

      if (response.request.responseURL !== requestURL) {
        // Since this endpoint redirects to login page when unauthorized and we cannot prevent redirection in browser JS,
        // we detect it by comparing URL of request and response.

        // Redirected means unauthorized.
        this.setAuthInfo(null);
        return;
      }

      if (
        response.status === 200 &&
        response.data &&
        response.data.isAuthenticated === true
      ) {
        // Authorized.
        this.setAuthInfo(response.data);
      } else {
        if (this.retryCount < 3) {
          this.retryCount++;
          this.getToken();
        } else {
          this.setAuthInfo(null);
        }
      }
    } catch (error) {
      this.setAuthInfo(null);
    }
  };

  hasToken = (): boolean => {
    return (
      this.authData !== null &&
      Date.parse(this.authData.expires_at) - Date.now() > 1000 &&
      window.localStorage.getItem(this.isLoggedInStorageKey) !== null &&
      window.localStorage.getItem(this.isLoggedInStorageKey) !== "false"
    );
  };

  setAuthInfo = (authInfo: TokenResponse | null) => {
    this.setIsLoggedIn(authInfo !== null);
    this.authData = authInfo;
  };

  private onStorageChanged = (e: StorageEvent) => {
    if (e.key !== this.isLoggedInStorageKey) {
      return;
    }

    // refresh value when storage flag changes
    this.getToken();
  };

  private setIsLoggedIn(value: boolean) {
    if (value) {
      window.localStorage.setItem(this.isLoggedInStorageKey, "true");
    } else {
      window.localStorage.removeItem(this.isLoggedInStorageKey);
    }

    this.isLoggedIn = value;
    auth.setIsLoggedIn(value);
    auth.setIsLoggedOutModalOn(!value);
  }
}
